import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ArticleViewerProps {
  content: string;
}

interface HeadingItem {
  id: string;
  text: string;
  level: number;
}

const ArticleViewer = ({ content }: ArticleViewerProps) => {
  const headings = useMemo(() => {
    const items: HeadingItem[] = [];
    const lines = content.split('\n');
    for (const line of lines) {
      const match = line.match(/^(#{1,3})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        const text = match[2].replace(/[*_`#]/g, '').trim();
        const id = text.toLowerCase().replace(/[^\w\sà-ú]/gi, '').replace(/\s+/g, '-');
        items.push({ id, text, level });
      }
    }
    return items;
  }, [content]);

  const showToc = headings.length >= 3;

  return (
    <div className="mt-6 px-2">
      {showToc && (
        <nav className="mb-8 p-4 bg-muted/50 rounded-lg border">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Índice
          </h4>
          <ul className="space-y-1.5">
            {headings.map((h, i) => (
              <li key={i} style={{ paddingLeft: `${(h.level - 1) * 16}px` }}>
                <a
                  href={`#${h.id}`}
                  className="text-sm text-primary hover:underline transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div className="prose-article break-words overflow-wrap-anywhere" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => {
              const text = String(children).replace(/[*_`#]/g, '').trim();
              const id = text.toLowerCase().replace(/[^\w\sà-ú]/gi, '').replace(/\s+/g, '-');
              return <h1 id={id} className="text-2xl font-bold mt-8 mb-4 text-foreground border-b pb-2 border-border">{children}</h1>;
            },
            h2: ({ children }) => {
              const text = String(children).replace(/[*_`#]/g, '').trim();
              const id = text.toLowerCase().replace(/[^\w\sà-ú]/gi, '').replace(/\s+/g, '-');
              return <h2 id={id} className="text-xl font-semibold mt-7 mb-3 text-foreground">{children}</h2>;
            },
            h3: ({ children }) => {
              const text = String(children).replace(/[*_`#]/g, '').trim();
              const id = text.toLowerCase().replace(/[^\w\sà-ú]/gi, '').replace(/\s+/g, '-');
              return <h3 id={id} className="text-lg font-medium mt-5 mb-2 text-foreground">{children}</h3>;
            },
            p: ({ children }) => <p className="mb-4 text-foreground/90 leading-relaxed text-[15px] break-words" style={{ overflowWrap: 'anywhere' }}>{children}</p>,
            ul: ({ children }) => <ul className="mb-4 ml-1 space-y-1.5">{children}</ul>,
            ol: ({ children }) => <ol className="mb-4 ml-1 space-y-1.5 list-decimal list-inside">{children}</ol>,
            li: ({ children }) => (
              <li className="flex items-start gap-2 text-foreground/90 text-[15px]">
                <span className="text-primary mt-1.5 shrink-0">•</span>
                <span className="flex-1">{children}</span>
              </li>
            ),
            strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
            em: ({ children }) => <em className="italic text-foreground/80">{children}</em>,
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                {children}
              </a>
            ),
            blockquote: ({ children }) => (
              <blockquote className="my-4 pl-4 border-l-4 border-primary/30 bg-muted/30 py-2 pr-4 rounded-r text-foreground/80 italic">
                {children}
              </blockquote>
            ),
            code: ({ className, children, ...props }) => {
              const isBlock = className?.includes('language-');
              if (isBlock) {
                return (
                  <code className={`block bg-slate-900 text-slate-100 rounded-lg p-4 my-4 text-sm font-mono overflow-x-auto whitespace-pre leading-relaxed ${className || ''}`}>
                    {children}
                  </code>
                );
              }
              return (
                <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-primary/90">
                  {children}
                </code>
              );
            },
            pre: ({ children }) => <pre className="my-4">{children}</pre>,
            table: ({ children }) => (
              <div className="my-4 overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">{children}</table>
              </div>
            ),
            thead: ({ children }) => <thead className="bg-muted/70">{children}</thead>,
            th: ({ children }) => <th className="px-4 py-2.5 text-left font-semibold text-foreground border-b border-border">{children}</th>,
            td: ({ children }) => <td className="px-4 py-2.5 text-foreground/90 border-b border-border/50">{children}</td>,
            tr: ({ children, ...props }) => <tr className="even:bg-muted/30 hover:bg-muted/50 transition-colors">{children}</tr>,
            hr: () => <hr className="my-6 border-border" />,
            del: ({ children }) => <del className="text-muted-foreground line-through">{children}</del>,
            input: ({ checked, ...props }) => (
              <input type="checkbox" checked={checked} readOnly className="mr-2 accent-primary" {...props} />
            ),
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default ArticleViewer;
